import { useContext, useMemo } from 'react';

import { AuthContext } from './auth.context';
import { AuthContextType } from '../../types/auth';

export const useAuthContext = (): AuthContextType => {
  const auth = useContext(AuthContext);

  if (!auth) throw new Error('useAuthContext is used outside of context');

  return auth;
};

export const useHasPermission = (permission: string): boolean => {
  const { currentUser } = useAuthContext();

  return useMemo(() => {
    if (!currentUser || !currentUser.permissions) {
      return false;
    }

    return currentUser.permissions.includes(permission);
  }, [currentUser, permission]);
};

/** Whether a feature deployed switched off is on for the workspace - its
 *  release date has come, or it was turned on early for this workspace
 *  (backend/app/feature_flags.py). Off until the user is loaded, so a gated
 *  feature never flashes into view. Only hides it: the backend refuses its
 *  actions on its own. */
export const useIsFeatureEnabled = (feature: string): boolean => {
  const { currentUser } = useAuthContext();

  return useMemo(() => {
    if (!currentUser || !currentUser.features) {
      return false;
    }

    return currentUser.features.includes(feature);
  }, [currentUser, feature]);
};

/** Whether the signed-in user is on the service side - a technician.
 *  Mirrors the backend's users.selectors.is_service_role_user: the flag lives
 *  on the permission groups they belong to, not on the user. */
export const useIsServiceRole = (): boolean => {
  const { currentUser } = useAuthContext();

  return useMemo(
    () => (currentUser?.groups ?? []).some((group) => group.is_service_role),
    [currentUser]
  );
};

/** The signed-in workspace's company name. Doubles as the location of
 *  equipment with no installation address: a machine that is not installed at
 *  a client's site is back with us. */
export const useTenantName = (): string => {
  const { currentUser } = useAuthContext();

  return currentUser?.tenant?.name?.trim() || '';
};

export const useHasAnyPermission = (permissions: string[]): boolean => {
  const { currentUser } = useAuthContext();

  return useMemo(() => {
    if (!currentUser || !currentUser.permissions) {
      return false;
    }

    return permissions.some((permission) => currentUser.permissions.includes(permission));
  }, [currentUser, permissions]);
};
